import React, { useEffect, useState } from "react";
import css from "./.module.scss";
import { useMutation, useQuery } from "@apollo/client";
import { INCREMENT_LISTING_ACTION } from "@/graphql/queries/listing";
import { usePath } from "@/contexts/path/path";
import useGetPhone from "@/components/Listing/ListingOwner/useGetPhone";
import { useAuthState } from "@/contexts/auth/auth.provider";
import { Modal, Skeleton } from "@aqar-app/react-ui";
import LoginAlert from "@/components/login-alert";
import {
  trackCallUser,
  trackShowNumber,
  trackWhatsappUser,
} from "@/lib/tracking/trackNavigationEvents";
import config from "@/common/config";
import { GET_USER_REVIEWS } from "@/graphql/queries/user";
import Image from "next/image";
import Link from "next/link";
import Icon from "@/components/icon";
import dynamic from "next/dynamic";

const UserReviewsModal = dynamic(
  () => import("@/components/Listing/ListingScreen/owner/UserReviewsModal"),
  { ssr: false },
);

const OneFraction = new Intl.NumberFormat("en", { maximumFractionDigits: 1 });
export function NewListingOwner({ listing }) {
  const { user, user_id: id, status, employee } = listing;
  const { name, img, review, type, company_name, company_logo } = user || {};
  const [phoneForCalls, setPhoneForCalls] = useState(null);
  const [incrementListingAction] = useMutation(INCREMENT_LISTING_ACTION);
  const path = usePath();
  const { getCallsPhone, getWhatsappPhone } = useGetPhone(listing);

  useEffect(() => {
    setPhoneForCalls(null);
  }, [listing]);
  const { token } = useAuthState();
  const whatsappButtonOnClick = async (e) => {
    if (!token) return Modal.open(<LoginAlert />)(e);

    trackWhatsappUser(
      path.previousPath,
      path.category?.ga_listing_type,
      path.category?.ga_property_category,
    );
    incrementListingAction({
      variables: { id: listing.id, type: "whatsapp" },
    });
    const phone = await getWhatsappPhone();
    if (!phone) return;
    if (phone.startsWith("http"))
      return window.open(phone, {
        target: "_blank",
      });
    return window.open(
      encodeURI(
        `${phone}?text=${`السلام عليكم\nأتواصل معك بخصوص إعلانكم على موقع عقار (${listing.title})\n${config.AQAR_WEB}/ad/${listing.id}`}`,
      ),
      {
        target: "_blank",
      },
    );
  };

  // eslint-disable-next-line consistent-return
  const phoneButtonOnClick = async (e) => {
    if (!token) return Modal.open(<LoginAlert />)(e);
    if (!phoneForCalls) {
      setPhoneForCalls(await getCallsPhone());
      trackShowNumber(
        path.previousPath,
        undefined,
        path.category?.ga_listing_type,
        path.category?.ga_property_category,
      );
    } else {
      incrementListingAction({
        variables: { id, type: "calls" },
      });

      trackCallUser(
        path.previousPath,
        undefined,
        path.category?.ga_listing_type,
        path.category?.ga_property_category,
      );

      window.open(`tel:${phoneForCalls}`, "_self");
    }
  };

  const {
    data: {
      User: {
        reviews: { total },
      },
    } = { User: { reviews: {} } },
    loading: loadingReview,
  } = useQuery(GET_USER_REVIEWS, {
    skip: !id,
    variables: {
      user_id: id,
    },
  });

  const renderReviews = () => {
    return (
      <>
        {loadingReview && <Skeleton className={css.ratingHolder} />}
        {review && total && (
          <div className={css.userReview}>
            <Icon width={15} height={15} name="v1/star" />
            <p
              className={css.reviewCount + " " + css.clickable}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                Modal.open(
                  <UserReviewsModal
                    reviewsCount={total}
                    review={OneFraction.format(review)}
                    userId={id}
                  />,
                )();
              }}
            >
              <span>{OneFraction.format(review)}</span>
              <span> التقييمات ({total})</span>
            </p>
          </div>
        )}
        {!loadingReview && !review && (
          <div className={css.userReview}>
            <p>لا يوجد تقييم بعد</p>
          </div>
        )}
      </>
    );
  };

  const userName = type === 1 && employee?.name ? employee.name : name;
  const userImg = type === 1 ? employee?.img : img;

  return (
    <div className={css.owner}>
      {type === 1 && (
        <div className={css.companyInfo}>
          {company_logo ? (
            <div className={css.ownerImg}>
              <Image
                unoptimized
                src={`${config.IMAGES_CDN_BASE_URL}users/${
                  company_logo || null
                }`}
                alt="Profile Image"
                width={40}
                height={40}
              />
            </div>
          ) : (
            <svg
              width="50"
              height="50"
              viewBox="0 0 50 50"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect
                x="0.5"
                y="0.5"
                width="49"
                height="49"
                rx="5.5"
                fill="#F9FAFB"
                stroke="#E1E6EB"
              />
              <path
                d="M12.9688 17.2116H19.8438V18.077C19.8438 19.5084 21.0005 20.6731 22.4219 20.6731H27.5782C28.9996 20.6731 30.1563 19.5084 30.1563 18.077V17.2116H37.0313C37.9955 17.2116 38.75 17.9715 38.75 18.9424V34.5193C38.75 35.4902 37.9955 36.2501 37.0313 36.2501H12.9688C12.0046 36.2501 11.25 35.4902 11.25 34.5193V18.9424C11.25 17.9887 12.0217 17.2116 12.9688 17.2116ZM18.9844 22.4039C17.563 22.4039 16.4063 23.5687 16.4063 25.0001C16.4063 26.4315 17.563 27.5962 18.9844 27.5962C20.4058 27.5962 21.5625 26.4315 21.5625 25.0001C21.5625 23.5687 20.4058 22.4039 18.9844 22.4039ZM25.8594 25.8655C25.385 25.8655 25 26.2531 25 26.7309C25 27.2086 25.385 27.5962 25.8594 27.5962H32.7344C33.2088 27.5962 33.5938 27.2086 33.5938 26.7309C33.5938 26.2531 33.2088 25.8655 32.7344 25.8655H25.8594ZM18.9844 28.4567C17.6646 28.4567 16.3439 28.7847 15.1417 29.4303C14.8616 29.5808 14.6875 29.8722 14.6875 30.1924V31.9232C14.6875 32.4009 15.0725 32.7885 15.5469 32.7885H22.4219C22.8963 32.7885 23.2813 32.4009 23.2813 31.9232V30.1924C23.2813 29.8722 23.1008 29.5808 22.8223 29.4303C21.6192 28.7847 20.3042 28.4567 18.9844 28.4567ZM25.8594 29.327C25.385 29.327 25 29.7147 25 30.1924C25 30.6701 25.385 31.0578 25.8594 31.0578H32.7344C33.2088 31.0578 33.5938 30.6701 33.5938 30.1924C33.5938 29.7147 33.2088 29.327 32.7344 29.327H25.8594Z"
                fill="#999999"
              />
              <path
                d="M25 13.75C26.8958 13.75 28.4375 15.3025 28.4375 17.2115V18.0769C28.4375 18.5546 28.0525 18.9423 27.5781 18.9423H22.4219C21.9475 18.9423 21.5625 18.5546 21.5625 18.0769V17.2115C21.5625 15.3025 23.1042 13.75 25 13.75Z"
                fill="#999999"
              />
            </svg>
          )}
          <h2 className={css.companyName}>{company_name || ""}</h2>
          {renderReviews()}
        </div>
      )}
      {id && <div style={{width: "100%"}}>
        <Link className={css.info} href={`/user/${id}`}>
          {userImg ? (
            <div className={css.ownerImg}>
              <Image
                unoptimized
                src={`${config.IMAGES_CDN_BASE_URL}users/${userImg || null}`}
                alt="Profile Image"
                width={40}
                height={40}
              />
            </div>
          ) : (
            <svg
              width="40"
              height="40"
              viewBox="0 0 40 40"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect
                x="0.5"
                y="0.5"
                width="39"
                height="39"
                rx="5.5"
                fill="#F9FAFB"
                stroke="#E1E6EB"
              />
              <path
                d="M29.4672 25.4701C31.1237 27.0919 30.385 29.7632 28.3731 30.9151C25.8418 32.3642 22.9587 33.1428 20 33.1428C17.0332 33.1428 14.1425 32.36 11.6062 30.9032C9.60663 29.7546 8.86375 27.1016 10.5028 25.4796C10.8459 25.14 11.2225 24.8318 11.6285 24.5604C13.0907 23.5828 14.8454 23.1414 16.5953 23.311H23.4047C25.1529 23.1517 26.9029 23.5972 28.3629 24.5733C28.7602 24.8389 29.1295 25.1395 29.4672 25.4701ZM26 12.8398C26 11.6565 25.6497 10.4998 24.9933 9.516C24.337 8.53215 23.4041 7.76534 22.3127 7.31252C21.2212 6.85971 20.0202 6.74123 18.8616 6.97207C17.7029 7.20292 16.6386 7.77271 15.8032 8.6094C14.9679 9.4461 14.399 10.5121 14.1685 11.6726C13.9381 12.8332 14.0563 14.0361 14.5084 15.1293C14.9605 16.2225 15.7261 17.1568 16.7084 17.8142C17.6907 18.4716 18.8455 18.8225 20.0269 18.8225C20.8113 18.8225 21.588 18.6677 22.3127 18.3671C23.0374 18.0664 23.6959 17.6257 24.2505 17.0702C24.8052 16.5146 25.2451 15.8551 25.5453 15.1293C25.8455 14.4034 26 13.6254 26 12.8398Z"
                fill="#999999"
              />
            </svg>
          )}
          <div className={css.textInfo}>
            <h2 className={css.name}>{userName || "مستخدم"}</h2>
            {type === 1 ? null : renderReviews()}
          </div>
          <Icon width={16} height={16} name="leftArrow-searches"/>
        </Link>
      </div>}

      <div className={css.btnSection}>
        {status !== 4 && (
          <button onClick={phoneButtonOnClick} className={css.callBtn}>
            <Icon
              className={`${css.callIcon} `}
              name="Phone-white"
              width={14}
              height={14}
            />
            <span className={css.callText}>{phoneForCalls ?? "إتصال"}</span>
          </button>
        )}
        <button
          onClick={whatsappButtonOnClick}
          className={`${css.callBtn} ${css.whatsapp}`}
        >
          <Icon
            className={css.callIcon}
            name="whatsapp-colored"
            width={14}
            height={14}
          />
          <span className={css.whatsappText}>واتساب</span>
        </button>
      </div>
    </div>
  );
}

export default NewListingOwner;
